/**
 * Maps Lexical editor theme classes to inline CSS styles.
 * Used during content rendering for email/PDF output where CSS classes
 * cannot be relied upon.
 */

export type ClassToStyleMap = {
  [className: string]: { [styleProp: string]: string };
};

// === TEXT FORMATTING ===
const textFormattingStyles: ClassToStyleMap = {
  PhxEditorTheme__textBold: { fontWeight: 'bold' },
  PhxEditorTheme__textItalic: { fontStyle: 'italic' },
  PhxEditorTheme__textUnderline: { textDecoration: 'underline' },
  PhxEditorTheme__textStrikethrough: { textDecoration: 'line-through' },
  PhxEditorTheme__textUnderlineStrikethrough: { textDecoration: 'underline line-through' },
  PhxEditorTheme__textSubscript: { fontSize: '0.8em', verticalAlign: 'sub' },
  PhxEditorTheme__textSuperscript: { fontSize: '0.8em', verticalAlign: 'super' },
  PhxEditorTheme__textCode: {
    backgroundColor: 'rgb(240, 242, 245)',
    padding: '1px 0.25rem',
    fontFamily: 'Menlo, Consolas, Monaco, monospace',
    fontSize: '94%',
  },
  PhxEditorTheme__hashtag: {
    backgroundColor: 'rgba(88, 144, 255, 0.15)',
    borderBottom: '1px solid rgba(88, 144, 255, 0.3)',
  },
};

// === LINKS & STRUCTURE ===
const structureStyles: ClassToStyleMap = {
  PhxEditorTheme__link: { color: 'rgb(33, 111, 219)', textDecoration: 'none' },
  PhxEditorTheme__code: {
    backgroundColor: 'rgb(240, 242, 245)',
    fontFamily: 'Menlo, Consolas, Monaco, monospace',
    display: 'block',
    padding: '8px 8px 8px 52px',
    lineHeight: '1.53',
    fontSize: '13px',
    margin: '0',
    marginTop: '8px',
    marginBottom: '8px',
    overflowX: 'auto',
    position: 'relative',
    tabSize: '2',
  },
  PhxEditorTheme__quote: {
    margin: '0',
    marginLeft: '20px',
    marginBottom: '10px',
    fontSize: '15px',
    color: 'rgb(101, 103, 107)',
    borderLeftColor: 'rgb(206, 208, 212)',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    paddingLeft: '16px',
  },
};

// === HEADINGS ===
const headingStyles: ClassToStyleMap = {
  PhxEditorTheme__h1: { fontSize: '24px', color: 'rgb(5, 5, 5)', fontWeight: '400', margin: '0' },
  PhxEditorTheme__h2: { fontSize: '15px', color: 'rgb(101, 103, 107)', fontWeight: '700', margin: '0' },
  PhxEditorTheme__h3: { fontSize: '12px', margin: '0' },
  // NOTE: Uses CSS variable - may not work in email/PDF contexts
  PhxEditorTheme__indent: { '--lexical-indent-base-value': '40px' },
};

// === TABLES ===
const tableStyles: ClassToStyleMap = {
  PhxEditorTheme__table: {
    borderCollapse: 'collapse',
    borderSpacing: '0',
    margin: '30px 0',
    tableLayout: 'fixed',
    width: '100%',
  },
  PhxEditorTheme__tableCell: {
    border: '1px solid #bbb',
    padding: '6px 8px',
    verticalAlign: 'top',
    textAlign: 'left',
    position: 'relative',
    outline: 'none',
    minWidth: '75px',
  },
  PhxEditorTheme__tableCellEditing: { boxShadow: '0 0 5px rgba(0, 0, 0, 0.4)', borderRadius: '3px' },
  PhxEditorTheme__tableSelected: { outline: '2px solid rgb(60, 132, 244)' },
  PhxEditorTheme__tableCellSelected: { backgroundColor: '#c9dbf0' },
  PhxEditorTheme__tableCellPrimarySelected: {
    border: '2px solid rgb(60, 132, 244)',
    position: 'absolute',
    width: 'calc(100% - 2px)',
    height: 'calc(100% - 2px)',
    left: '-1px',
    top: '-1px',
    zIndex: '2',
  },
  PhxEditorTheme__tableCellResizer: { position: 'absolute', right: '-4px', width: '8px' },
  PhxEditorTheme__tableAddColumns: { backgroundColor: '#eee' },
  PhxEditorTheme__tableAddRows: { backgroundColor: '#eee' },
};

// === LAYOUT & MISC ===
const layoutStyles: ClassToStyleMap = {
  PhxEditorTheme__hr: { border: 'none', margin: '1em 0', cursor: 'pointer' },
  PhxEditorTheme__layoutContainer: { display: 'grid', gap: '10px', margin: '10px 0' },
  PhxEditorTheme__layoutItem: { border: '1px dashed #ddd', padding: '8px 16px' },
  PhxEditorTheme__autocomplete: { color: '#ccc' },
  PhxEditorTheme__characterLimit: { backgroundColor: '#ffbbbb', display: 'inline' },
};

/**
 * Complete mapping of all PhxEditorTheme classes to inline styles.
 * Total: 29 class mappings
 */
export const defaultClassToStyleMap: ClassToStyleMap = {
  ...textFormattingStyles,
  ...structureStyles,
  ...headingStyles,
  ...tableStyles,
  ...layoutStyles,
};
